import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import Button from '@/components/common/Button'
import toast from 'react-hot-toast'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa'

interface ContactFormData {
  name: string
  email: string
  phone?: string
  subject: string
  message: string
}

const Contact = () => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>()

  const onSubmit = (data: ContactFormData) => {
    // TODO: Send to backend
    console.log(data)
    toast.success(t('contact.messageSent'))
    reset()
  }

  return (
    <div className="bg-dark-950 min-h-screen py-12 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-gradient">{t('contact.title')}</span>
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-6 text-gradient">{t('contact.contactInfo')}</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white flex-shrink-0">
                    <FaEnvelope className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('contact.email')}</div>
                    <div className="text-base text-gray-200">info@kennzeichenpro24.de</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white flex-shrink-0">
                    <FaPhone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('contact.phone')}</div>
                    <div className="text-base text-gray-200">+49 (0) XXX XXX XXX</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white flex-shrink-0">
                    <FaMapMarkerAlt className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('contact.address')}</div>
                    <div className="text-base text-gray-200">Deutschland</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white flex-shrink-0">
                    <FaClock className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t('contact.workingHours')}</div>
                    <div className="text-base text-gray-200">{t('contact.workingHoursMonFri')}</div>
                    <div className="text-base text-gray-200">{t('contact.workingHoursSat')}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="card-hover"
            >
              <h2 className="text-2xl font-bold mb-6 text-gradient">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.name')} *
                  </label>
                  <input
                    {...register('name', { required: t('contact.nameRequired') })}
                    type="text"
                    className="input-field"
                  />
                  {errors.name && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.email')} *
                  </label>
                  <input
                    {...register('email', {
                      required: t('contact.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('contact.emailInvalid'),
                      },
                    })}
                    type="email"
                    className="input-field"
                  />
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.phone')}
                  </label>
                  <input
                    {...register('phone')}
                    type="tel"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.subject')} *
                  </label>
                  <input
                    {...register('subject', { required: t('contact.subjectRequired') })}
                    type="text"
                    className="input-field"
                  />
                  {errors.subject && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    {...register('message', { required: t('contact.messageRequired') })}
                    rows={4}
                    className="input-field resize-none"
                  />
                  {errors.message && (
                    <p className="mt-1.5 text-sm text-red-400">{errors.message.message}</p>
                  )}
                </div>

                <Button type="submit" className="w-full">
                  {t('contact.send')}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
